import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type {
  Table as DataTableInstance,
  InitialTableState,
  Row as DataTableRow,
} from "@tanstack/react-table";
import React from "react";

import { useWpxTableSetup } from "./hooks/useWpxTableSetup";
import TableHeader from "./TableHeader";
import TableRow, { EmptyTableRow } from "./TableRow";
import type {
  FilterType,
  WpxTableEmptyState,
  WpxTableFeature,
  WpxTableFunctions,
  WpxTableHideConfig,
  WpxTablePropsClassNames,
  WpxTableSlotConfig,
  WpxTableUiConfig,
} from "./types/dataTable.types";
import WpxTableFilterControls from "./WpxTableFilterControls";
import WpxTablePaginationControls from "./WpxTablePaginationControls";
import WpxTableToolbarControls from "./WpxTableToolbarControls";

import { ZERO } from "@/app/constants/constants";
import type { ResponsePagination } from "@/components/pagination/hooks/usePagination";
import EmptyData from "@/components/statePages/EmptyData";
import * as Table from "@/components/ui/table";
import type { ExtendedColumnDef } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

export type {
  FilterConfig,
  FilterType,
  WpxTableFeature,
  WpxTableFunctions,
  WpxTableUiConfig,
} from "./types/dataTable.types";

export interface WpxTableProps<
  TData,
  TValue,
  TQueryResponse,
  TQueryBaseParams,
> {
  title: string;
  rows?: TData[];
  columns: ExtendedColumnDef<TData, TValue>[];
  empty: WpxTableEmptyState;

  filters?: FilterType<TQueryResponse, TQueryBaseParams> | boolean;
  toolbar?: WpxTableFunctions<TData, TValue>;
  pagination?: ResponsePagination;

  feature?: WpxTableFeature;
  ui?: WpxTableUiConfig;
  hide?: WpxTableHideConfig;
  slots?: WpxTableSlotConfig;
  /** @deprecated Use slots.customEmptyLayout instead. */
  customEmptyLayout?: React.ReactNode;
  /** @deprecated Use slots instead. */
  customProperties?: WpxTableSlotConfig;
  /** @deprecated Use slots.floatingBar instead. */
  floatingBar?: React.ReactNode | null;

  hasWritePermission?: boolean;
  onTableInstanceChange?: (instance: DataTableInstance<TData>) => void;
  id?: string;
  initialState?: InitialTableState;

  columnDraggable?: boolean;
  priorityColumnIds?: string[];
  isTablePaginated?: boolean;
  enableColumnResizing?: boolean;
  enableEnhancedUX?: boolean;
  scopeByActiveTab?: boolean;
  isLoading?: boolean;
  showTitle?: boolean;
  showSelectedCount?: boolean;
  showTotalCount?: boolean;
  filterClearButtonSize?: "sm" | "lg";
  classNames?: WpxTablePropsClassNames;
  hideFilterRow?: boolean;
  children?: (controls: WpxTableCompositionControls) => React.ReactNode;
  rowDndConfig?: {
    enabled: boolean;
    dragHandleColumnId: string;
    getRowId: (row: { original: TData }) => string;
    isRowDisabled?: (row: { original: TData }) => boolean;
    sensors?: unknown;
    modifiers?: unknown[];
    onDragStart?: (event: unknown) => void;
    onDragOver?: (event: unknown) => void;
    onDragCancel?: () => void;
    onDragEnd?: (event: unknown) => void;
    dragOverlay?: React.ReactNode;
  };
}

export interface WpxTableCompositionControls {
  filters: React.ReactNode;
  toolbar: React.ReactNode;
  floatingBar: React.ReactNode;
  table: React.ReactNode;
  pagination: React.ReactNode;
}

/**
 * Shared WorkplanX table surface.
 *
 * Keeps feature toolbar actions, table UI state, and server-driven pagination
 * in separate contracts so feature modules can adopt a consistent table setup
 * without rebuilding the same orchestration.
 *
 * @param {WpxTableProps<TData, TValue, TQueryResponse, TQueryBaseParams>} props - Table rendering inputs.
 * @returns {React.JSX.Element} The rendered WorkplanX table.
 */
export const WpxTable = <
  TData extends { id?: string },
  TValue,
  TQueryResponse,
  TQueryBaseParams,
>(
  props: WpxTableProps<TData, TValue, TQueryResponse, TQueryBaseParams>,
): React.JSX.Element => {
  const {
    title,
    rows,
    columns,
    empty,
    filters,
    toolbar,
    pagination,
    feature,
    ui,
    slots,
    customEmptyLayout,
    customProperties,
    floatingBar,
    hasWritePermission = false,
    onTableInstanceChange,
    id,
    columnDraggable,
    priorityColumnIds,
    isTablePaginated = true,
    hide,
    filterClearButtonSize,
    classNames,
    enableColumnResizing = false,
    isLoading: isLoadingProp = false,
    initialState,
    showTitle = false,
    showSelectedCount,
    showTotalCount,
    enableEnhancedUX,
    hideFilterRow = false,
    scopeByActiveTab,
    children,
    rowDndConfig,
  } = props;

  const resolveSlot = <K extends keyof WpxTableSlotConfig>(
    key: K,
    topLevelLegacyValue?: WpxTableSlotConfig[K],
  ): WpxTableSlotConfig[K] | undefined => {
    if (slots && key in slots) {
      return slots[key];
    }

    if (topLevelLegacyValue !== undefined) {
      return topLevelLegacyValue;
    }

    return customProperties?.[key];
  };

  const resolvedSlots: WpxTableSlotConfig = {
    customEmptyLayout: resolveSlot("customEmptyLayout", customEmptyLayout),
    filterControls: resolveSlot("filterControls"),
    floatingBar: resolveSlot("floatingBar", floatingBar),
    handleAddIds: resolveSlot("handleAddIds"),
    newRowSlot: resolveSlot("newRowSlot"),
    paginationControls: resolveSlot("paginationControls"),
    toolbarControls: resolveSlot("toolbarControls"),
  };

  const {
    table,
    filterOperation,
    isLoading,
    isColumnDraggable,
    isEnhancedUxEnabled,
    filteredColumns,
    columnOrder,
    toolbar: toolbarConfig,
    resolvedRows,
    resolvedUi,
    resolvedPagination,
    ref,
    skin,
    isMobile,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    shouldRenderPaginator,
    totalPages,
    hasToolbarActions,
    hasColumnReordered,
    totalResultCount,
    resetColumnOrder,
    handleDragEnd,
    sensors,
  } = useWpxTableSetup({
    title,
    rows,
    columns,
    toolbar,
    pagination,
    feature,
    ui,
    hide,
    filterClearButtonSize,
    classNames,
    enableColumnResizing,
    isLoading: isLoadingProp,
    initialState,
    showTitle,
    showSelectedCount,
    showTotalCount,
    enableEnhancedUX,
    columnDraggable,
    priorityColumnIds,
    isTablePaginated,
    onTableInstanceChange,
    scopeByActiveTab,
  });

  const [activeColumnId, setActiveColumnId] = React.useState<string | null>(
    null,
  );

  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map((col) => col.id)
    .join(",");
  const visibleResultCount = resolvedRows.length;

  const rowSortableItems = React.useMemo(() => {
    if (!rowDndConfig?.enabled) {
      return [];
    }

    return table
      .getRowModel()
      .rows.map((row) => {
        const rowId = rowDndConfig.getRowId(
          row as unknown as { original: TData },
        );

        return rowId || row.id;
      })
      .filter(Boolean);
  }, [rowDndConfig, table]);

  const getRowDndId = React.useCallback(
    (targetRow: DataTableRow<TData>): string => {
      if (!rowDndConfig?.getRowId) {
        return targetRow.id;
      }

      return rowDndConfig.getRowId(targetRow as unknown as { original: TData });
    },
    [rowDndConfig?.getRowId],
  );

  const isRowDragDisabled = React.useCallback(
    (targetRow: DataTableRow<TData>): boolean => {
      if (!rowDndConfig?.isRowDisabled) {
        return false;
      }

      return Boolean(
        rowDndConfig.isRowDisabled(targetRow as unknown as { original: TData }),
      );
    },
    [rowDndConfig?.isRowDisabled],
  );

  const renderEmptyContent = (): React.ReactNode => {
    const addButton = toolbarConfig?.add || undefined;

    if (resolvedSlots.customEmptyLayout) {
      return resolvedSlots.customEmptyLayout;
    }

    if (resolvedSlots.newRowSlot) {
      return "";
    }

    return (
      <EmptyData
        hideButton={!hasWritePermission}
        title={empty.title || title}
        small={empty.small}
        subtitle={empty.subtitle}
        btnAction={empty.btnAction || addButton?.handleClick}
        btnLabel={empty.btnLabel || addButton?.label}
        btnVariant={empty.btnVariant || addButton?.variant}
      />
    );
  };

  const renderTableRows = (): React.ReactNode => {
    if (!table.getRowModel().rows.length && !resolvedRows.length) {
      return (
        <EmptyTableRow
          hideEmptyRow={Boolean(resolvedSlots.newRowSlot)}
          getContent={renderEmptyContent}
        />
      );
    }

    return table.getRowModel().rows.map((row, rowIndex) => {
      return (
        <React.Fragment key={row.id}>
          <TableRow
            rowIdx={rowIndex}
            row={row}
            isSelected={row.getIsSelected()}
            onRowClick={toolbarConfig?.onRowClick}
            RightClickData={
              hasWritePermission ? toolbarConfig?.RightClickData : undefined
            }
            isMobile={isMobile}
            className={cn(
              resolvedUi.classNames?.tableRowClassName,
              resolvedUi.classNames?.getTableRowClassName?.(
                row as unknown as { original: unknown },
              ),
            )}
            columnDraggable={isColumnDraggable}
            rowDraggable={Boolean(rowDndConfig?.enabled)}
            rowDragHandleColumnId={rowDndConfig?.dragHandleColumnId}
            getRowDndId={rowDndConfig?.getRowId ? getRowDndId : undefined}
            isRowDragDisabled={
              rowDndConfig?.isRowDisabled ? isRowDragDisabled : undefined
            }
            enableEnhancedUX={isEnhancedUxEnabled}
            activeColumnId={activeColumnId}
            cellRenderKey={filteredColumns}
            columnOrder={columnOrder}
            visibleColumnIds={visibleColumnIds}
            columnsDependency={filteredColumns}
          />
          {toolbarConfig?.enableRowExpansion &&
            row.getIsExpanded() &&
            toolbarConfig.expandedRowComponent && (
              <Table.TableRow>
                <Table.TableCell
                  colSpan={filteredColumns.length}
                  className='bg-gray-50/50 p-0 dark:bg-gray-900/50'
                >
                  <div className='p-4'>
                    {toolbarConfig.expandedRowComponent(row.original)}
                  </div>
                </Table.TableCell>
              </Table.TableRow>
            )}
        </React.Fragment>
      );
    });
  };

  const tableContent = (
    <div className='relative overflow-auto rounded-md border'>
      <Table.Table
        id={id}
        className={cn(
          "w-full",
          (resolvedUi.enableColumnResizing || isLoading) && "table-fixed",
        )}
      >
        <Table.TableHeader
          className={cn(
            resolvedRows.length > ZERO && "group/header",
            skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
          )}
        >
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableHeader
                key={headerGroup.id}
                headerGroup={headerGroup}
                pagination={resolvedPagination}
                columnOrder={columnOrder}
                dataLength={resolvedRows.length}
                isMobile={isMobile}
                enableColumnResizing={resolvedUi.enableColumnResizing}
                columnDraggable={isColumnDraggable}
                isLoading={isLoading}
                rowSelection={table.getState().rowSelection}
              />
            );
          })}
        </Table.TableHeader>
        <Table.TableBody ref={ref}>
          {rowDndConfig?.enabled ? (
            <SortableContext
              items={rowSortableItems}
              strategy={verticalListSortingStrategy}
            >
              {renderTableRows()}
            </SortableContext>
          ) : (
            renderTableRows()
          )}
          {resolvedSlots.newRowSlot ?? null}
        </Table.TableBody>
      </Table.Table>
    </div>
  );

  const filterControls =
    resolvedSlots.filterControls ??
    (filters ? (
      <WpxTableFilterControls
        filters={filters as FilterType<TQueryResponse, TQueryBaseParams>}
        showFilters={!hideFilterRow}
        filterOperation={filterOperation}
        hide={resolvedUi.hideFilters}
        clearButtonSize={resolvedUi.clearButtonSize}
      />
    ) : null);

  const toolbarControls =
    resolvedSlots.toolbarControls ??
    (hasToolbarActions ? (
      <WpxTableToolbarControls
        table={table}
        columns={filteredColumns}
        data={resolvedRows}
        hasWritePermission={hasWritePermission}
        filterOperation={filterOperation}
        functions={toolbarConfig}
        hide={resolvedUi.hideToolbar}
        hasColumnReordered={hasColumnReordered}
        resetColumnOrder={resetColumnOrder}
      />
    ) : null);

  const paginationControls = resolvedSlots.paginationControls ?? (
    <WpxTablePaginationControls
      currentPage={currentPage}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      isLoading={isLoading}
      isTablePaginated={isTablePaginated}
      pageSize={pageSize}
      shouldRenderPaginator={shouldRenderPaginator}
      showSelectedCount={resolvedUi.showSelectedCount}
      showTotalCount={resolvedUi.showTotalCount}
      table={table}
      totalPages={totalPages}
      totalResultCount={totalResultCount}
      visibleResultCount={visibleResultCount}
    />
  );

  const draggableTableContent = isColumnDraggable ? (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[
        restrictToHorizontalAxis,
        restrictToFirstScrollableAncestor,
        restrictToParentElement,
      ]}
      onDragStart={(event) => {
        setActiveColumnId(String(event.active.id));
      }}
      onDragEnd={(event) => {
        setActiveColumnId(null);
        handleDragEnd(event);
      }}
      onDragCancel={() => {
        setActiveColumnId(null);
      }}
      sensors={sensors}
      autoScroll
    >
      {tableContent}
    </DndContext>
  ) : (
    tableContent
  );

  const interactiveTableContent = rowDndConfig?.enabled ? (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={(rowDndConfig.modifiers as never[]) ?? []}
      onDragStart={
        rowDndConfig.onDragStart
          ? (event): void =>
              rowDndConfig.onDragStart?.(event as unknown as never)
          : undefined
      }
      onDragOver={
        rowDndConfig.onDragOver
          ? (event): void =>
              rowDndConfig.onDragOver?.(event as unknown as never)
          : undefined
      }
      onDragCancel={
        rowDndConfig.onDragCancel
          ? (): void => rowDndConfig.onDragCancel?.()
          : undefined
      }
      onDragEnd={
        rowDndConfig.onDragEnd
          ? (event): void => rowDndConfig.onDragEnd?.(event as unknown as never)
          : undefined
      }
      sensors={rowDndConfig.sensors as never}
      autoScroll
    >
      {draggableTableContent}
      {rowDndConfig.dragOverlay ?? null}
    </DndContext>
  ) : (
    draggableTableContent
  );

  if (children) {
    return (
      <div className='w-full'>
        {resolvedUi.showTitle && (
          <h3 className='mb-4 text-lg font-semibold'>{title}</h3>
        )}
        {children({
          filters: filterControls,
          toolbar: toolbarControls,
          floatingBar: resolvedSlots.floatingBar,
          table: interactiveTableContent,
          pagination: paginationControls,
        })}
      </div>
    );
  }

  return (
    <div className='w-full'>
      {resolvedUi.showTitle && (
        <h3 className='mb-4 text-lg font-semibold'>{title}</h3>
      )}
      {(Boolean(filters) ||
        hasToolbarActions ||
        resolvedSlots.filterControls ||
        resolvedSlots.toolbarControls) && (
        <div className='flex flex-wrap-reverse justify-between gap-4'>
          {resolvedSlots.filterControls ?? filterControls}
          {(hasToolbarActions || resolvedSlots.toolbarControls) && (
            <div className='mb-2 ml-auto'>
              {resolvedSlots.toolbarControls ?? toolbarControls}
            </div>
          )}
        </div>
      )}
      {resolvedSlots.floatingBar}

      {interactiveTableContent}

      {paginationControls}
    </div>
  );
};
